import { HttpRequest, HttpResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { BankRepository } from '@asm/features/sync/repositories/bank.repository';
import { PaymentConditionRepository } from '@asm/features/sync/repositories/payment-condition.repository';
import { PaymentMethodRepository } from '@asm/features/sync/repositories/payment-method.repository';

/**
 * Handler offline para dados de pagamento.
 *
 * Cobre:
 * - GET /payment-method/select
 * - GET /payment-condition/select?paymentMethodCode=&cartTotal=
 * - GET /bank/select
 */
@Injectable({ providedIn: 'root' })
export class PaymentOfflineHandler {
  private readonly paymentMethodRepo = inject(PaymentMethodRepository);
  private readonly paymentConditionRepo = inject(PaymentConditionRepository);
  private readonly bankRepo = inject(BankRepository);

  canHandle(req: HttpRequest<unknown>): boolean {
    return (
      req.method === 'GET' &&
      (req.url.includes('/payment-method/select') ||
        req.url.includes('/payment-condition/select') ||
        req.url.includes('/bank/select'))
    );
  }

  async handle(req: HttpRequest<unknown>): Promise<HttpResponse<unknown>> {
    const { url } = req;

    if (url.includes('/payment-method/select')) {
      return new HttpResponse({ status: 200, body: await firstValueFrom(this.paymentMethodRepo.getAll()) });
    }

    if (url.includes('/payment-condition/select')) {
      const paymentMethodCode = req.params.get('paymentMethodCode') ?? '';
      const cartTotalRaw = req.params.get('cartTotal');
      const cartTotal = cartTotalRaw ? Number(cartTotalRaw) : undefined;
      const all = await firstValueFrom(this.paymentConditionRepo.getAll());
      return new HttpResponse({ status: 200, body: this.filterPaymentConditions(all, paymentMethodCode, cartTotal) });
    }

    if (url.includes('/bank/select')) {
      return new HttpResponse({ status: 200, body: await firstValueFrom(this.bankRepo.getAll()) });
    }

    throw new Error(`[PaymentOfflineHandler] Rota não mapeada: ${req.method} ${url}`);
  }

  /**
   * Filtra condições de pagamento pelo método e valor mínimo de parcela.
   *
   * - Se `paymentMethodCode` estiver presente, só retorna condições cujo array
   *   `paymentMethods` contenha esse código.
   * - Se a condição tiver `minimumInstallmentAmount`, o valor por parcela
   *   (`cartTotal / installmentCount`) deve ser ≥ ao mínimo.
   */
  filterPaymentConditions(items: any[], paymentMethodCode: string, cartTotal?: number): any[] {
    return items.filter(item => {
      if (!paymentMethodCode) return true;

      const rawPaymentMethods = item?.paymentMethods;
      if (Array.isArray(rawPaymentMethods) && rawPaymentMethods.length > 0) {
        const matchesMethod = rawPaymentMethods.some((entry: any) => {
          const candidate = entry?.paymentMethod ?? entry;
          return this.matchesScalarValue(candidate, paymentMethodCode);
        });
        if (!matchesMethod) return false;
      }

      return this.matchesInstallmentAmount(item, cartTotal);
    });
  }

  private matchesInstallmentAmount(item: any, cartTotal?: number): boolean {
    const minimumInstallmentAmount = Number(item?.minimumInstallmentAmount ?? 0);
    const installmentCount = Number(item?.installmentCount ?? 0);

    if (!minimumInstallmentAmount || !installmentCount || installmentCount <= 0) return true;

    return (cartTotal ?? 0) / installmentCount >= minimumInstallmentAmount;
  }

  private matchesScalarValue(source: unknown, expected: string): boolean {
    if (typeof source === 'string') return source === expected;

    if (source && typeof source === 'object') {
      const candidate = source as { code?: string; description?: string; name?: string; value?: string };
      return [candidate.code, candidate.description, candidate.name, candidate.value].some(v => v === expected);
    }

    return false;
  }
}
